import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ClipboardList, Calendar, GitBranch, CalendarPlus, Clock, MapPin, Stethoscope, Quote } from "lucide-react";
import { API } from "@/config/api";
import { getRepDisplayName } from "@/config/constants";
import { DayPlanner } from "@/components/DayPlanner";

const QUOTES = [
  { quote: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
  { quote: "The harder you work for something, the greater you'll feel when you achieve it.", author: "Unknown" },
  { quote: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { quote: "Sales are contingent upon the attitude of the salesman, not the attitude of the prospect.", author: "W. Clement Stone" },
  { quote: "Every sale has five basic obstacles: no need, no money, no hurry, no desire, no trust.", author: "Zig Ziglar" },
  { quote: "Your attitude, not your aptitude, will determine your altitude.", author: "Zig Ziglar" },
  { quote: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { quote: "It always seems impossible until it's done.", author: "Nelson Mandela" },
  { quote: "Opportunities don't happen. You create them.", author: "Chris Grosser" },
  { quote: "Great things in business are never done by one person; they're done by a team of people.", author: "Steve Jobs" },
  { quote: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
  { quote: "The best way to predict the future is to create it.", author: "Peter Drucker" },
  { quote: "A goal is a dream with a deadline.", author: "Napoleon Hill" },
  { quote: "You don't have to be great to start, but you have to start to be great.", author: "Zig Ziglar" },
  { quote: "Success usually comes to those who are too busy to be looking for it.", author: "Henry David Thoreau" },
  { quote: "The difference between a successful person and others is not a lack of strength, not a lack of knowledge, but rather a lack of will.", author: "Vince Lombardi" },
  { quote: "Push yourself because no one else is going to do it for you.", author: "Unknown" },
  { quote: "Dream big and dare to fail.", author: "Norman Vaughan" },
  { quote: "What you lack in talent can be made up with desire, hustle and giving 110% all the time.", author: "Don Zimmer" },
  { quote: "Make each day your masterpiece.", author: "John Wooden" }
];

const getGreeting = () => {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Good morning";
  if (h >= 12 && h < 17) return "Good afternoon";
  if (h >= 17 && h < 21) return "Good evening";
  return "Working late";
};

const getDayOfYear = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  return Math.floor((now - start) / (1000 * 60 * 60 * 24));
};

const todayFormatted = () => new Date().toLocaleDateString("en-ZA", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const todayISO = () => new Date().toISOString().split("T")[0];

const RepHomePage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({ visitsWeek: "—", apptsMonth: "—", referralsMonth: "—" });
  const [todayAppts, setTodayAppts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(r => setUser(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    const repName = user.assigned_rep_name || user.name;
    const fetchData = async () => {
      setLoading(true);
      // Stats
      try {
        const res = await axios.get(`${API}/crm/reports/rep-performance`, { withCredentials: true });
        const repData = res.data?.by_rep?.[repName];
        if (repData) {
          setStats({
            visitsWeek: repData.total_visits_week ?? repData.total_visits ?? 0,
            apptsMonth: repData.appointments_month ?? repData.total_cases ?? 0,
            referralsMonth: repData.total_cases ?? 0,
          });
        }
      } catch { /* graceful */ }

      // Today's appointments
      try {
        const res = await axios.get(`${API}/upcoming?days=1&rep_name=${encodeURIComponent(repName)}`, { withCredentials: true });
        const today = todayISO();
        setTodayAppts((Array.isArray(res.data) ? res.data : []).filter(a => a.appointment_date === today));
      } catch { setTodayAppts([]); }

      setLoading(false);
    };
    fetchData();
  }, [user]);

  const quote = QUOTES[getDayOfYear() % QUOTES.length];
  const repDisplayName = getRepDisplayName(user?.assigned_rep_name || user?.name || "");

  return (
    <div className="p-4 lg:p-6 space-y-5 pb-6" data-testid="rep-home-page">
      {/* Section 1 — Greeting */}
      <div className="rounded-xl bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-6 sm:p-8 text-white">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <p className="text-blue-200 text-sm">{getGreeting()},</p>
            <h1 className="text-2xl sm:text-3xl font-bold mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>
              {repDisplayName}
            </h1>
            <p className="text-blue-200 text-sm mt-2">{todayFormatted()}</p>
          </div>
        </div>
        <div className="mt-6 pt-5 border-t border-white/20">
          <div className="flex items-start gap-3">
            <Quote className="w-5 h-5 text-blue-300 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm italic text-blue-100 leading-relaxed">"{quote.quote}"</p>
              <p className="text-xs text-blue-300 mt-1.5">— {quote.author}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 1b — Day at a glance */}
      <DayPlanner />

      {/* Section 2 — Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Visits This Week</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {loading ? "..." : stats.visitsWeek}
                </p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
                <ClipboardList className="w-4 h-4 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Appointments</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {loading ? "..." : stats.apptsMonth}
                </p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-emerald-500 flex items-center justify-center shrink-0">
                <Calendar className="w-4 h-4 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">Referrals</p>
                <p className="text-2xl font-bold mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>
                  {loading ? "..." : stats.referralsMonth}
                </p>
              </div>
              <div className="w-9 h-9 rounded-lg bg-purple-500 flex items-center justify-center shrink-0">
                <GitBranch className="w-4 h-4 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 3 — Today's Schedule */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: "Manrope, sans-serif" }}>
            <Clock className="w-4 h-4 text-blue-500" />
            Today's Schedule
            <Badge variant="secondary" className="ml-auto text-[10px]">{todayFormatted()}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-3 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : todayAppts.length > 0 ? (
            <div className="space-y-2">
              {todayAppts.map((a, i) => (
                <div key={a.appointment_id || i} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="text-center min-w-[52px]">
                      <p className="text-sm font-bold" style={{ fontFamily: "Manrope, sans-serif" }}>
                        {a.appointment_time || "TBD"}
                      </p>
                    </div>
                    <div className="border-l pl-3">
                      <div className="flex items-center gap-1.5">
                        <Stethoscope className="w-3.5 h-3.5 text-primary" />
                        <span className="text-sm font-medium">{a.doctor_name}</span>
                      </div>
                      {a.practice_name && <p className="text-xs text-muted-foreground">{a.practice_name}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.visit_type && <Badge variant="secondary" className="text-[10px]">{a.visit_type}</Badge>}
                    {a.area && (
                      <Badge variant="outline" className="text-[10px]">
                        <MapPin className="w-2.5 h-2.5 mr-0.5" />
                        {a.area}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground font-medium">No appointments scheduled for today</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 gap-2"
                onClick={() => navigate("/rep-calendar")}
              >
                <CalendarPlus className="w-4 h-4" />
                Plan an Appointment
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section 4 — Quick Actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          className="h-14 text-sm font-medium gap-2"
          onClick={() => navigate("/visits")}
          data-testid="quick-action-log-visit"
        >
          <ClipboardList className="w-5 h-5" />
          Log a Visit
        </Button>
        <Button
          variant="outline"
          className="h-14 text-sm font-medium gap-2"
          onClick={() => navigate("/rep-calendar")}
          data-testid="quick-action-plan-appointment"
        >
          <CalendarPlus className="w-5 h-5" />
          Plan an Appointment
        </Button>
      </div>
    </div>
  );
};

export default RepHomePage;
